import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
} from "@mui/x-data-grid";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import {
  FoodQuantity,
  PaginatedSearchResultsFoodQuantity,
} from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "models/routes";
import DataGridListBox from "components/common/ui/DataGridListBox";
import IngredientStockListTableToolbar from "components/inventory/ui/stockLevelList/IngredientStockListTableToolbar";

interface IngredientStockListTableProps {}

const IngredientStockListTable: FC<IngredientStockListTableProps> = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<FoodQuantity[]>([]);
  const [rowCount, setRowCount] = useState(0);

  // Parse pagination from URL params
  const paginationModel = useMemo(() => {
    const page = parseInt(searchParams.get("page") || "0", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    return { page, pageSize };
  }, [searchParams]);

  /**
   * Handle pagination model change
   */
  const setPaginationModel = useCallback(
    (newModel: { page: number; pageSize: number }): void => {
      const newParams = new URLSearchParams(searchParams);
      const oldPageSize = paginationModel.pageSize;
      const oldPage = paginationModel.page;

      // Check if pageSize has changed
      if (newModel.pageSize !== oldPageSize) {
        // Calculate the first item index on the old page
        const firstItemIndex = oldPage * oldPageSize;
        // Calculate the equivalent page number for the new pageSize
        const equivalentPage = Math.floor(firstItemIndex / newModel.pageSize);
        newParams.set("page", equivalentPage.toString());
      } else {
        // If pageSize hasn't changed, just use the new page number
        newParams.set("page", newModel.page.toString());
      }

      newParams.set("pageSize", newModel.pageSize.toString());
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, paginationModel],
  );

  // Parse keywords from URL params
  const keywords = useMemo(
    () => searchParams.get("keywords") || undefined,
    [searchParams],
  );

  /**
   * Handle keywords change
   */
  const setKeywords = useCallback(
    (newKeywords: string | undefined): void => {
      const urlParams = new URLSearchParams(searchParams);

      if (newKeywords) {
        urlParams.set("keywords", newKeywords);
      } else {
        urlParams.delete("keywords");
      }

      // Reset page to 0 when keywords change
      urlParams.set("page", "0");

      setSearchParams(urlParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /**
   * Initialize default URL params if not present
   */
  useEffect(() => {
    const hasPage = searchParams.has("page");
    const hasPageSize = searchParams.has("pageSize");

    if (!hasPage || !hasPageSize) {
      const newParams = new URLSearchParams(searchParams);
      if (!hasPage) {
        newParams.set("page", "0");
      }
      if (!hasPageSize) {
        newParams.set("pageSize", "10");
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /**
   * Get the first batch of ingredient keys
   */
  useEffect(() => {
    jspPlatformApi()
      .inventory.getInventoryStocksSearch({
        query: {
          keywords: keywords || undefined,
          number_per_page: paginationModel.pageSize,
          page_index: paginationModel.page,
          only_procured_items: true,
        },
      })
      .then((res) => {
        const data = res.data as PaginatedSearchResultsFoodQuantity;
        setRows(data?.data ?? []);
        setRowCount(data?.total_counts ?? -1);
      })
      .catch((reason) => {
        if (
          isApiError(reason) &&
          isErrorResponseBodyType(toJspApiError(reason).body)
        ) {
          // console.log(toJspApiError(reason).body.detail);
        }
      });
  }, [keywords, paginationModel.page, paginationModel.pageSize]);

  /**
   * Go to ingredient stock change details page
   * @param id The grid row ID which should be the ingredient's food ID
   */
  const handleViewDetailsClick = (id: GridRowId) => () => {
    navigate(`${Routes.INVENTORY_DETAILS}/${id}`, {
      replace: false,
      state: { foodId: id },
    });
  };

  const columns: GridColDef[] = [
    {
      field: "editAction",
      type: "actions",
      width: 45,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const to = `${Routes.INVENTORY_DETAILS}/${id}`;
        return [
          <GridActionsCellItem
            key={id}
            icon={<FolderOpenIcon />}
            label="View details"
            className="textPrimary"
            onClick={(e) => {
              // Handle Ctrl/Cmd + click to open in new tab
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                window.open(to, "_blank");
              } else {
                handleViewDetailsClick(id)();
              }
            }}
            onMouseDown={(e) => {
              // Handle middle-click (wheel click) to open in new tab
              if (e.button === 1) {
                e.preventDefault();
                window.open(to, "_blank");
              }
            }}
            color="inherit"
          />,
        ];
      },
    },
    {
      field: "amount",
      type: "number",
      headerName: "Stock",
      width: 110,
      editable: false,
      valueFormatter: (value: number) => `${value.toLocaleString()} g `,
    },
    {
      field: "food_name",
      headerName: "Ingredient",
      flex: 180,
      editable: false,
    },
    { field: "food_id", headerName: "Food ID", width: 120, editable: false },
  ];

  return (
    <DataGridListBox>
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        getRowId={(row: FoodQuantity) => row.food_id}
        slots={{
          toolbar: IngredientStockListTableToolbar,
        }}
        slotProps={{
          toolbar: {
            setKeywords,
          },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        rowCount={rowCount}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
      />
    </DataGridListBox>
  );
};

export default IngredientStockListTable;
